import type { Portal } from './types';

/**
 * The only routes the guidance engine may NAVIGATE to (CONTRACT §4).
 *
 * ── What makes a route safe ─────────────────────────────────────────────────
 * Every entry is one the P0 maps (`docs/map-*.md`, "mount safety") show sends
 * GET requests only when it opens, and is a view — not a create, edit or entry
 * form. The engine reaches it through the router (no reload, no arbitrary
 * URL). A route with `:params` is record-bound: the engine fills them only from
 * the route the user is already on, never with an ID it chose, and the server
 * still decides whether the record may be seen.
 *
 * Tours never change business data: no POST/PUT/PATCH/DELETE, no mutation
 * hook, no `element.click()` on a business control, no form submission. Being
 * listed here grants no authority of any kind.
 *
 * ── What is NOT here ────────────────────────────────────────────────────────
 * `EXCLUDED_ROUTES` below, each with its reason. Some of them allow a guide on
 * the page the user already has open (`currentPageGuide`), never navigation to
 * or away from it. Also left out, because no P0 map has verified what they
 * send on mount: `/organization/*`, `/admin/currencies`, `/admin/tax-codes`,
 * `/admin/catalogue`, `/suppliers`, `/suppliers/:id`, `/agreements` and
 * `/agreements/:id`. They join only after they are mapped.
 *
 * Sign-in (the tenant `LoginPage`, `VendorLogin`, `PlatformLogin`) and every
 * MFA challenge, enrolment and recovery-code screen are not routes: each
 * replaces its shell, and `GuidanceRoot` is mounted only inside a shell's
 * authenticated branch, so no guidance exists on them at all.
 */
export interface RoutePattern {
  portal: Portal;
  /** A react-router path pattern: static segments and `:param` segments. */
  path: string;
}

export const SAFE_ROUTES = {
  // ── Tenant workspace (map-tenant-buying §3, map-tenant-procurement §1) ────
  'tenant.dashboard': { portal: 'tenant', path: '/dashboard' },
  'tenant.catalogue': { portal: 'tenant', path: '/catalogue' },
  'tenant.catalogue-item': { portal: 'tenant', path: '/catalogue/:id' },
  'tenant.my-requests': { portal: 'tenant', path: '/my/requests' },
  'tenant.request': { portal: 'tenant', path: '/requests/:id' },
  'tenant.approvals': { portal: 'tenant', path: '/approvals' },
  'tenant.approval': { portal: 'tenant', path: '/approvals/:id' },
  'tenant.purchase-orders': { portal: 'tenant', path: '/purchase-orders' },
  'tenant.purchase-order': { portal: 'tenant', path: '/purchase-orders/:id' },
  'tenant.receiving': { portal: 'tenant', path: '/receiving' },
  'tenant.sourcing': { portal: 'tenant', path: '/sourcing' },
  'tenant.rfq': { portal: 'tenant', path: '/sourcing/:id' },
  'tenant.onboarding': { portal: 'tenant', path: '/onboarding' },
  'tenant.import': { portal: 'tenant', path: '/onboarding/imports/:id' },
  'tenant.reports': { portal: 'tenant', path: '/reports' },
  'tenant.report': { portal: 'tenant', path: '/reports/:id' },
  'tenant.report-records': { portal: 'tenant', path: '/reports/records/:dataset' },
  'tenant.budgets': { portal: 'tenant', path: '/budgets' },
  'tenant.budget': { portal: 'tenant', path: '/budgets/:id' },
  'tenant.approval-rules': { portal: 'tenant', path: '/admin/approval-rules' },
  'tenant.users': { portal: 'tenant', path: '/admin/users' },
  'tenant.roles': { portal: 'tenant', path: '/admin/roles' },
  'tenant.delegations': { portal: 'tenant', path: '/admin/delegations' },
  'tenant.purchasing-units': { portal: 'tenant', path: '/admin/purchasing-units' },
  'tenant.audit': { portal: 'tenant', path: '/admin/audit' },

  // ── Supplier portal (map-vendor §2) ──────────────────────────────────────
  'vendor.home': { portal: 'vendor', path: '/vendor' },
  'vendor.orders': { portal: 'vendor', path: '/vendor/orders' },
  'vendor.order': { portal: 'vendor', path: '/vendor/orders/:id' },
  'vendor.rfqs': { portal: 'vendor', path: '/vendor/rfqs' },
  'vendor.rfq': { portal: 'vendor', path: '/vendor/rfqs/:id' },
  'vendor.agreements': { portal: 'vendor', path: '/vendor/agreements' },
  'vendor.agreement': { portal: 'vendor', path: '/vendor/agreements/:id' },
  'vendor.profile': { portal: 'vendor', path: '/vendor/profile' },

  // ── Operator console (map-operator-auth §1) ──────────────────────────────
  'platform.overview': { portal: 'platform', path: '/platform' },
  'platform.organisations': { portal: 'platform', path: '/platform/organisations' },
  'platform.organisation': { portal: 'platform', path: '/platform/organisations/:id' },
} as const satisfies Record<string, RoutePattern>;

export type SafeRouteId = keyof typeof SAFE_ROUTES;

export interface ExcludedRoute {
  id: string;
  /** `null` for the public routes that sit outside every shell. */
  portal: Portal | null;
  path: string;
  reason: string;
  /**
   * Whether a guide may run on this page when the USER already opened it —
   * explaining what is there, never navigating to it or away from it, never
   * submitting it. False for anything that shows or collects a secret.
   */
  currentPageGuide: boolean;
}

export const EXCLUDED_ROUTES = [
  {
    id: 'tenant.cart',
    portal: 'tenant',
    path: '/cart',
    reason:
      'Opening it (and `?step=review`, which refetches) sends POST /requests/:id/validate; its fields save on blur with PATCH/PUT.',
    currentPageGuide: true,
  },
  {
    id: 'tenant.request-edit',
    portal: 'tenant',
    path: '/requests/:id/edit',
    reason: 'The cart in amend mode: the same validate POST on mount and the same blur saves.',
    currentPageGuide: true,
  },
  {
    id: 'tenant.off-catalogue-new',
    portal: 'tenant',
    path: '/requests/off-catalogue/new',
    reason: 'A create form holding unsaved local input.',
    currentPageGuide: true,
  },
  {
    id: 'tenant.rfq-new',
    portal: 'tenant',
    path: '/sourcing/new',
    reason: 'A create form (POST /sourcing/rfqs on submit) holding unsaved local input.',
    currentPageGuide: true,
  },
  {
    id: 'tenant.receive',
    portal: 'tenant',
    path: '/receiving/:id',
    reason:
      'A receipt entry form ("record, form" in the P0 map), with a one-click "everything arrived" POST.',
    currentPageGuide: true,
  },
  {
    id: 'tenant.approval-rule-new',
    portal: 'tenant',
    path: '/admin/approval-rules/new',
    reason: 'A create form (the rule builder).',
    currentPageGuide: true,
  },
  {
    id: 'tenant.approval-rule',
    portal: 'tenant',
    path: '/admin/approval-rules/:id',
    reason: 'An edit form (the rule builder); `simulator` also matches this pattern.',
    currentPageGuide: true,
  },
  {
    id: 'tenant.approval-simulator',
    portal: 'tenant',
    path: '/admin/approval-rules/simulator',
    reason: 'An input form; its preview POST is user-pressed.',
    currentPageGuide: true,
  },
  {
    id: 'tenant.settings',
    portal: 'tenant',
    path: '/admin/settings',
    reason: 'The page is an edit form saved with PATCH.',
    currentPageGuide: true,
  },
  {
    id: 'tenant.organization-profile',
    portal: 'tenant',
    path: '/admin/organization',
    reason: 'The page is an edit form saved with PATCH.',
    currentPageGuide: true,
  },
  {
    id: 'tenant.account-security',
    portal: 'tenant',
    path: '/account/security',
    reason: 'MFA: setup key, QR and recovery codes.',
    currentPageGuide: false,
  },
  {
    id: 'vendor.account',
    portal: 'vendor',
    path: '/vendor/account',
    reason: 'Supplier password change: collects the current and replacement password.',
    currentPageGuide: false,
  },
  {
    id: 'platform.organisation-new',
    portal: 'platform',
    path: '/platform/organisations/new',
    reason:
      'A create form (POST /platform/tenants on submit); only a role with platform.tenant.provision gets the form.',
    currentPageGuide: true,
  },
  {
    id: 'platform.security',
    portal: 'platform',
    path: '/platform/security',
    reason: 'MFA: setup key, QR and recovery codes.',
    currentPageGuide: false,
  },
  {
    id: 'public.secure-action',
    portal: null,
    path: '/actions/:token',
    reason: 'A secure link: the token is the authority, and a refused token writes an audit row.',
    currentPageGuide: false,
  },
  {
    id: 'public.activation',
    portal: null,
    path: '/activate/:token',
    reason: 'Setting a password on an invited account.',
    currentPageGuide: false,
  },
  {
    id: 'public.login',
    portal: null,
    path: '/login',
    reason: 'Sign-in.',
    currentPageGuide: false,
  },
] as const satisfies readonly ExcludedRoute[];

type ExcludedEntry = (typeof EXCLUDED_ROUTES)[number];

export type ExcludedRouteId = ExcludedEntry['id'];

/** Excluded routes on which a guide may still explain the page the user opened. */
export type CurrentPageRouteId = Extract<ExcludedEntry, { currentPageGuide: true }>['id'];

/**
 * A page a guide can be MATCHED to: a safe route, or a current-page-only route.
 * Navigation always takes a `SafeRouteId`, never this.
 */
export type GuidePageId = SafeRouteId | CurrentPageRouteId;

/** An opaque public identifier or a report/dataset key: no `/`, `.`, `%`, `?` or `#`. */
const PARAM_VALUE = /^[A-Za-z0-9][A-Za-z0-9_-]*$/;

export function isSafeRouteId(value: unknown): value is SafeRouteId {
  return typeof value === 'string' && Object.hasOwn(SAFE_ROUTES, value);
}

/**
 * The path for a safe route, with its params filled in.
 *
 * Throws — rather than returning something the engine might follow — for an
 * unknown ID, a missing, extra or malformed param, or a result that an
 * excluded route would claim (`/sourcing/new` is not an enquiry called "new").
 */
export function safeRoutePath(
  id: SafeRouteId,
  params: Readonly<Record<string, string>> = {},
): string {
  if (!isSafeRouteId(id)) throw new Error(`Unknown safe route: ${String(id)}`);

  const pattern = SAFE_ROUTES[id].path;
  const wanted = paramNames(pattern);
  const extra = Object.keys(params).filter((name) => !wanted.includes(name));

  if (extra.length > 0) throw new Error(`Safe route ${id} takes no param(s): ${extra.join(', ')}`);

  const path = pattern
    .split('/')
    .map((segment) => {
      if (!segment.startsWith(':')) return segment;

      const name = segment.slice(1);
      const value = Object.hasOwn(params, name) ? params[name] : undefined;

      if (value === undefined) throw new Error(`Safe route ${id} needs the param "${name}"`);
      if (!PARAM_VALUE.test(value)) throw new Error(`Safe route ${id}: invalid "${name}"`);

      return value;
    })
    .join('/');

  const claimed = excludedRouteFor(path);

  if (claimed) throw new Error(`Safe route ${id} would open the excluded route ${claimed.id}`);

  return path;
}

/**
 * The excluded route a pathname falls on, if any.
 *
 * Matches as the router does: case-insensitive static segments, a trailing
 * slash ignored, and percent-encoding decoded first.
 */
export function excludedRouteFor(pathname: string): ExcludedEntry | undefined {
  return EXCLUDED_ROUTES.find((route) => matchRoutePattern(route.path, pathname) !== null);
}

export interface GuidePageMatch {
  id: GuidePageId;
  params: Readonly<Record<string, string>>;
}

/**
 * The guide page a pathname is, with its params — or `null` where no guidance
 * may run at all (a secret-bearing, token or sign-in route, or an unmapped one).
 *
 * An excluded route wins over a safe pattern that would also match it:
 * `/sourcing/new` is the enquiry form, not an enquiry called "new".
 */
export function matchGuidePage(pathname: string): GuidePageMatch | null {
  for (const route of EXCLUDED_ROUTES) {
    const params = matchRoutePattern(route.path, pathname);

    if (params === null) continue;

    return route.currentPageGuide === true ? { id: route.id, params } : null;
  }

  for (const id of Object.keys(SAFE_ROUTES) as SafeRouteId[]) {
    const params = matchRoutePattern(SAFE_ROUTES[id].path, pathname);

    if (params !== null) return { id, params };
  }

  return null;
}

/** The pattern's path params for this pathname, or `null` when it does not match. */
export function matchRoutePattern(
  pattern: string,
  pathname: string,
): Record<string, string> | null {
  const want = splitPath(pattern);
  const have = splitPath(pathname);

  if (want === null || have === null || want.length !== have.length) return null;

  const params: Record<string, string> = {};

  for (let index = 0; index < want.length; index += 1) {
    const segment = want[index];
    const actual = have[index];

    if (segment.startsWith(':')) {
      if (actual === '') return null;

      params[segment.slice(1)] = actual;
    } else if (segment.toLowerCase() !== actual.toLowerCase()) {
      return null;
    }
  }

  return params;
}

function paramNames(pattern: string): string[] {
  return pattern
    .split('/')
    .filter((segment) => segment.startsWith(':'))
    .map((segment) => segment.slice(1));
}

function splitPath(path: string): string[] | null {
  const trimmed = path.length > 1 ? path.replace(/\/+$/, '') : path;

  try {
    return trimmed.split('/').map((segment) => decodeURIComponent(segment));
  } catch {
    // A malformed escape: the router would not match it either.
    return null;
  }
}
